import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Send, Eye, Search, FileCheck2, Wallet, AlertTriangle, CircleSlash, RefreshCw, Clock, Zap } from "lucide-react";
import { api, argent, ouvrirPdf } from "./api";
import { useConfirmation } from "./Confirmation";
import { Tableau, Cellule, Badge, Section, Case, BarreSelection, BoutonSombre, CarteIcone, ATonAttention, pointsATraiter, TONS_BLOC } from "./ui";
import { Encart, Repartition } from "./DashboardPlus";
import { SelecteurSaison } from "./SelecteurSaison";

const FILTRES = [
  { id: "pretes", label: "Prêtes à envoyer — facture payée" },
  { id: "attend_paiement", label: "En attente du paiement" },
  { id: "a_renvoyer", label: "Nouvelles à envoyer — facture corrigée et partie" },
  { id: "a_relancer", label: "N° AVS à relancer" },
  { id: "relance_recente", label: "Relancés récemment" },
  { id: "bloquees", label: "Sans n° AVS" },
  { id: "a_zero", label: "À zéro — non envoyées" },
  { id: "envoyees", label: "Déjà envoyées" },
  { id: "tous", label: "Tous" },
];

const DELAI_RELANCE = 30;

// Même distinction que pour les relances de numéros AVS : le dossier Jeunesse+Sport
// ne concerne que les 2-20 ans, et l'école traite les enfants d'abord.
const PUBLICS = [
  { id: "enfants", label: "Enfants" },
  { id: "adultes", label: "Adultes" },
  { id: "toutes", label: "Tous les âges" },
];

// Le public par défaut dépend de la vue : une attestation prête part à tout le monde,
// une relance de numéro AVS ne concerne que le dossier Jeunesse+Sport (2-20 ans).
const PUBLIC_PAR_DEFAUT = {
  pretes: "toutes", envoyees: "toutes", tous: "toutes", a_zero: "toutes",
  a_renvoyer: "toutes", attend_paiement: "toutes",
  a_relancer: "enfants", relance_recente: "enfants", bloquees: "enfants",
};

// Dates en clair, comme partout ailleurs dans la console : 25.09.2026, jamais 2026-09-25.
const jour = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");

// En cherchant un élève, l'école veut lire d'un coup d'œil SI l'attestation est partie,
// et sinon pourquoi : le paiement, le n° AVS ou la facture en correction. Un mot sur la
// pastille, le détail juste en dessous — la colonne reste étroite.
// Le serveur donne maintenant le statut complet (`l.statut`) : un mot, ce qui se passe,
// et QUAND l'attestation part ou ce qui la bloque (école, 14.10.2026). Le calcul local ne
// sert plus que de secours.
const statutAttestation = (l) => {
  if (l.statut) return { ...l.statut, testid: `attest-statut-${l.statut.cle}` };
  if (l.a_renvoyer) return { ton: "rouge", mot: "à renvoyer",
    detail: l.nouvelle_prete_le ? `nouvelle prête le ${jour(l.nouvelle_prete_le)}`
      : `envoyée le ${jour(l.envoyee_le)} · facture corrigée depuis`, testid: "attest-a-renvoyer" };
  if (l.envoyee_le) return { ton: "primaire", mot: "envoyée",
    detail: `le ${jour(l.envoyee_le)}`, testid: "attest-envoyee" };
  if (l.a_zero) return { ton: "ambre", mot: "pas envoyée",
    detail: "rien à attester", testid: "attest-non-envoyee" };
  if (l.a_envoyer) return { ton: "vert", mot: "prête à envoyer",
    detail: `pas encore envoyée${l.facture_payee_le ? ` · payée le ${jour(l.facture_payee_le)}` : ""}`,
    testid: "attest-prete" };
  if (!l.avs) return { ton: "ambre", mot: "pas envoyée",
    detail: "n° AVS manquant", testid: "attest-non-envoyee" };
  if (l.facture_en_chantier) return { ton: "ambre", mot: "pas envoyée",
    detail: "facture en correction", testid: "attest-non-envoyee" };
  return { ton: "ambre", mot: "pas envoyée",
    detail: "facture pas encore payée", testid: "attest-non-envoyee" };
};

const StatutAttestation = ({ ligne, onApercu }) => {
  const s = statutAttestation(ligne);
  return (
    <>
      <Badge ton={s.ton} testid={s.testid}>{s.mot}</Badge>
      <div className="text-[11px] text-[var(--muted)] mt-1" data-testid="attest-statut-detail">{s.detail}</div>
      {s.prevu && (
        <div className="text-[11px] mt-1 flex items-start gap-1 leading-snug max-w-[16rem]"
          style={{ color: s.prevu_auto ? "#047857" : "#B45309" }} data-testid="attest-statut-prevu">
          <Clock size={12} className="shrink-0 mt-[1px]" /> <span>{s.prevu}</span>
        </div>
      )}
      <button className="btn-ghost mt-1" data-testid="attest-apercu" title="Aperçu du PDF"
        style={{ padding: "4px 10px" }} onClick={onApercu}>
        <Eye size={13} /> Aperçu
      </button>
    </>
  );
};

const joursDepuis = (iso) => {
  const d = new Date(String(iso || "").slice(0, 10));
  if (Number.isNaN(d.getTime())) return 9999;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

// « Ouvrir » sur une alerte de « À traiter » arrive ici avec « vue:élève » (Console.jsx) :
// l'écran s'ouvre sur la vue de l'alerte (« Nouvelles attestations à envoyer » → « Nouvelles
// à envoyer ») et la ligne de l'élève est mise en avant — il s'ouvrait sur la vue par
// défaut, « Prêtes à envoyer », où la ligne n'est pas (école, 10.10.2026).
const lireCible = (cible) => {
  const m = /^([a-z_]+):(.*)$/.exec(String(cible || ""));
  return m && FILTRES.some((f) => f.id === m[1]) ? { vue: m[1], eleve: m[2] } : null;
};

/* Chaque élève dans UNE case (les vues se recoupent : une attestation à 0 CHF peut aussi
   manquer de n° AVS) — la même règle que le tableau de bord, pour que la barre fasse le total. */
const caseDe = (l) => (l.envoyee_le ? (l.a_renvoyer ? "a_refaire" : "envoyee")
  : l.a_zero ? "a_zero" : !l.avs ? "sans_avs" : l.a_envoyer ? "prete"
  : l.attend_paiement ? "attend_paiement" : "facture_en_correction");
const TEINTE = TONS_BLOC.attestations;

export default function Attestations({ onChangement, cible, aTraiter }) {
  const { informer } = useConfirmation();
  const [data, setData] = useState(null);
  const vise = lireCible(cible);
  const [filtre, setFiltre] = useState(vise?.vue || "pretes");
  const [public_, setPublic] = useState(vise ? PUBLIC_PAR_DEFAUT[vise.vue] || "toutes" : "toutes");
  const [choisis, setChoisis] = useState([]);
  const [recherche, setRecherche] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [semestre, setSemestre] = useState(0);
  const [saison, setSaison] = useState("");
  // Progression de l'envoi groupé : « 12 / 48 envoyées ». Sans elle, l'écran restait muet
  // plus d'une minute et on croyait à un blocage.
  const [progres, setProgres] = useState(null);

  const charger = useCallback(async () => {
    const params = new URLSearchParams();
    if (semestre) params.set("semestre", String(semestre));
    if (saison) params.set("saison", saison);
    const suite = params.toString();
    const d = await api(`/attestations${suite ? `?${suite}` : ""}`);
    setData(d);
    setSemestre(d.semestre);
    setSaison(d.saison || "");
  }, [semestre, saison]);
  useEffect(() => { charger(); }, [charger]);

  if (!data) return <p className="text-[var(--muted)]">Chargement…</p>;

  // Un élève est « à relancer » s'il n'a toujours pas de n° AVS et qu'aucune demande
  // n'est partie depuis 30 jours : c'est la seule liste sur laquelle on appuie.
  const aRelancer = (l) => !l.avs && (!l.avs_demande_le || joursDepuis(l.avs_demande_le) >= DELAI_RELANCE);
  // La recherche passe AVANT les onglets : on cherche « Suzanne » dans toute l'école, sans
  // devoir deviner si son attestation est déjà partie ou si son numéro AVS manque.
  const q = recherche.trim().toLowerCase();
  const correspond = (l) => !q || [l.eleve, l.responsable, l.email, l.avs, ...(l.cours || [])]
    .some((v) => String(v || "").toLowerCase().includes(q));
  const items = (q ? data.items.filter(correspond) : data.items).filter((l) =>
    (q ? true
      : filtre === "tous" ? true
      : filtre === "a_renvoyer" ? l.a_renvoyer
      : filtre === "envoyees" ? l.envoyee_le
      : filtre === "bloquees" ? !l.avs
      : filtre === "a_zero" ? (l.a_zero && !l.envoyee_le)
      : filtre === "a_relancer" ? aRelancer(l)
      : filtre === "attend_paiement" ? l.attend_paiement
      : filtre === "relance_recente" ? (!l.avs && l.avs_demande_le && joursDepuis(l.avs_demande_le) < DELAI_RELANCE)
      : l.a_envoyer)
    && (q ? true : public_ === "toutes" ? true : public_ === "enfants" ? l.enfant : !l.enfant)
  );
  const tous = items.length > 0 && items.every((l) => choisis.includes(l.eleve_id));
  // Les enfants sans numéro AVS : c'est ce que montre l'onglet « Sans n° AVS », et donc ce
  // que la vignette doit expliquer.
  const sansAvsEnfants = data.items.filter((l) => !l.avs && l.enfant).length;
  // On ne redemande jamais un numéro AVS à qui l'a déjà donné.
  const dejaAvs = data.items.filter((l) => choisis.includes(l.eleve_id) && l.avs);
  const relancables = items.filter(aRelancer);
  // Le compteur d'un onglet doit annoncer ce qu'on verra EN CLIQUANT : chaque onglet
  // impose son public par défaut (les relances ne concernent que les enfants). Sans ça,
  // « N° AVS à relancer (9) » comptait 9 adultes puis affichait une liste vide.
  const compteur = (id) => {
    const pub = id === filtre ? public_ : (PUBLIC_PAR_DEFAUT[id] || "toutes");
    return data.items.filter((l) =>
      (id === "tous" ? true
        : id === "a_renvoyer" ? l.a_renvoyer
        : id === "envoyees" ? l.envoyee_le
        : id === "bloquees" ? !l.avs
        : id === "a_zero" ? (l.a_zero && !l.envoyee_le)
        : id === "a_relancer" ? aRelancer(l)
        : id === "attend_paiement" ? l.attend_paiement
        : id === "relance_recente" ? (!l.avs && l.avs_demande_le && joursDepuis(l.avs_demande_le) < DELAI_RELANCE)
        : l.a_envoyer)
      && (pub === "toutes" ? true : pub === "enfants" ? l.enfant : !l.enfant)).length;
  };

  const agir = async (quoi) => {
    setOccupe(true);
    try {
      if (quoi === "avs") {
        // La console enchaîne les paquets toute seule : plus besoin de sélectionner
        // 25 familles, de retenir où on s'est arrêté, puis de recommencer.
        let refs = choisis, envois = [], tropTot = 0, delai = 30, reel = true, tours = 0;
        while (refs.length && tours < 40) {
          const paquet = await api("/notifications/avs/demander",
            { method: "POST", body: JSON.stringify({ refs }) });
          envois = [...envois, ...paquet.envois];
          tropTot = paquet.trop_tot;
          delai = paquet.delai_jours;
          reel = paquet.mode_reel;
          if (!paquet.reste || paquet.envois.length === 0) break;
          const partis = new Set(paquet.envois.map((e) => e.ref));
          refs = refs.filter((x) => !partis.has(x));
          tours += 1;
        }
        const r = { envois, trop_tot: tropTot, delai_jours: delai, mode_reel: reel };
        await informer({
          titre: `${r.envois.length} demande(s) de n° AVS envoyée(s)`, ton: "succes",
          texte: r.mode_reel ? "Un seul e-mail par famille."
            : "MODE TEST : rien n'est parti aux familles.",
          points: r.trop_tot
            ? [`${r.trop_tot} famille(s) écartée(s) : déjà relancée(s) il y a moins de ${r.delai_jours} jours.`] : [],
        });
      } else {
        // L'envoi part en tâche de fond : on suit la progression seconde par seconde.
        const { lot, total } = await api("/attestations/envoyer",
          { method: "POST", body: JSON.stringify({ refs: choisis, semestre, saison }) });
        setProgres({ faits: 0, total });
        let etape = null;
        for (;;) {
          await new Promise((r) => setTimeout(r, 1000));
          etape = await api(`/attestations/envoi/${lot}`);
          if (!etape.suivi_perdu) setProgres({ faits: etape.faits, total: etape.total });
          if (etape.fini) break;
        }
        setProgres(null);
        if (etape.suivi_perdu || etape.interrompu) {
          // Le serveur a redémarré pendant l'envoi : ce n'est pas un échec. Les
          // attestations déjà parties portent leur statut, la liste le montre.
          await informer({
            titre: etape.interrompu
              ? `${etape.envois.length} attestation(s) envoyée(s) — suivi interrompu`
              : "Envoi terminé, suivi perdu",
            ton: "attention",
            texte: "Le serveur a perdu le fil de la progression (redémarrage pendant l'envoi). "
              + "Les attestations parties sont marquées « envoyée ».",
            points: ["Vérifie l'onglet « Déjà envoyées ».",
              "Ce qui reste dans « Prêtes à envoyer » n'est pas parti : relance-le."],
          });
        } else {
          await informer({
            titre: `${etape.envois.length} attestation(s) envoyée(s)`,
            ton: etape.echecs.length ? "attention" : "succes",
            texte: etape.mode_reel ? "Les familles ont reçu leur attestation."
              : "MODE TEST : rien n'est parti aux familles.",
            points: etape.echecs.map((e) => `${e.ref.split("::").pop()} — ${e.motif}`),
          });
        }
      }
      setChoisis([]);
      await charger();
      onChangement();
    } catch (e) {
      await informer({ titre: "Action interrompue", ton: "danger", texte: e.message });
    }
    setOccupe(false);
  };

  // Ouvrir une vue de la liste depuis une carte ou « À ton attention », et y descendre.
  const ouvrirVue = (id) => {
    setFiltre(id); setPublic(PUBLIC_PAR_DEFAUT[id] || "toutes"); setChoisis([]); setRecherche("");
    setTimeout(() => document.getElementById("attest-liste")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const cases = data.items.reduce((n, l) => ({ ...n, [caseDe(l)]: (n[caseDe(l)] || 0) + 1 }), {});
  const pourcentEnvoyees = data.total ? Math.round((100 * data.envoyees) / data.total) : 0;
  // L'envoi automatique de 6h15 : traite-t-il cette vue, et quand passe-t-il ?
  const auto = data.auto || {};
  const renvoisPrets = data.a_renvoyer_prets ?? data.a_renvoyer;
  const renvoisEnAttente = Math.max(0, (data.a_renvoyer || 0) - renvoisPrets);
  const attention = pointsATraiter(aTraiter, ["attestations"], (g) => {
    const vue = (g.items || []).map((i) => i.cible?.vue).find((v) => FILTRES.some((f) => f.id === v));
    ouvrirVue(vue || (g.id.startsWith("avs") ? "a_relancer" : "pretes"));
  });

  return (
    <div className="space-y-6" data-testid="console-attestations">
      {/* L'en-tête aux couleurs de la section « Attestations » du tableau de bord. */}
      <div className="rounded-3xl border px-5 md:px-6 py-5" style={{ background: TEINTE.doux, borderColor: TEINTE.bord }}>
        <div className="flex flex-wrap items-center gap-4">
          <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: TEINTE.accent, color: "#fff" }}>
            <ShieldCheck size={24} />
          </span>
          <div className="min-w-[14rem] flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: TEINTE.texte }}>Assurance maladie · tarif 595</div>
            <h1 className="font-display text-2xl md:text-3xl tracking-tight" style={{ color: "#0F1B2D" }}>Attestations</h1>
            <p className="text-sm mt-1 max-w-3xl" style={{ color: TEINTE.texte }}>
              L'attestation permet à la famille de se faire rembourser une partie des cours par sa
              complémentaire. Elle exige le numéro AVS de l'élève : c'est donc ici, et pas dans la
              facturation, qu'on relance les numéros manquants.
            </p>
          </div>
        </div>
        {/* Une attestation PAR SEMESTRE ET PAR SAISON : les familles s'inscrivent ou se
            désinscrivent d'un semestre à l'autre, et une nouvelle année repart de zéro. */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <SelecteurSaison saison={saison} onChange={(s) => { setSaison(s); setChoisis([]); }} />
          {[1, 2].map((s) => (
            <button key={s} data-testid={`attest-semestre-${s}`}
              onClick={() => { setSemestre(s); setChoisis([]); }}
              className="px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-transform hover:-translate-y-0.5"
              style={{
                background: data.semestre === s ? TEINTE.accent : "#fff",
                color: data.semestre === s ? "#fff" : "var(--muted)",
                borderColor: data.semestre === s ? TEINTE.accent : "var(--border)",
              }}>
              Semestre {s}
            </button>
          ))}
          <span className="text-xs" style={{ color: TEINTE.texte }}>
            {data.etiquette ? `${data.etiquette} · ` : ""}période attestée : celle du calendrier des séances (Réglages)
          </span>
        </div>
        {/* Ce qui part tout seul, et quand : chaque ligne de la liste le redit pour son élève. */}
        {data.auto && (
          <div className="mt-3 inline-flex items-start gap-2 rounded-xl px-3 py-2 text-xs bg-white border"
            style={{ borderColor: TEINTE.bord, color: auto.ok ? "#047857" : "#B45309" }} data-testid="attest-auto">
            <Zap size={14} className="shrink-0 mt-[1px]" />
            <span>
              {auto.ok
                ? `Envoi automatique allumé : prochain passage ${auto.quand}. Il envoie les attestations prêtes (et les nouvelles, une fois la facture corrigée payée) et redemande le n° AVS aux enfants tous les 30 jours.`
                : `Pas d'envoi automatique pour cette vue : ${auto.raison}. Les attestations partent avec « Envoyer ».`}
            </span>
          </div>
        )}
      </div>

      <ATonAttention points={attention} testid="attest-attention" />

      {progres && (
        <div className="bg-white rounded-2xl border p-4" style={{ borderColor: "var(--border)" }}
          data-testid="attest-progression">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">Envoi en cours…</span>
            <span className="text-[var(--muted)]">{progres.faits} / {progres.total} envoyée(s)</span>
          </div>
          <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "#EEF2F6" }}>
            <div className="h-full transition-all"
              style={{ width: `${Math.round((progres.faits / Math.max(progres.total, 1)) * 100)}%`,
                       background: "var(--primary)" }} />
          </div>
          <p className="text-xs text-[var(--muted)] mt-2">
            Tu peux laisser cet écran ouvert : l'envoi continue même si tu changes de page.
          </p>
        </div>
      )}

      {/* Les chiffres, en cartes de couleur comme sur le tableau de bord. Un clic ouvre la
          vue correspondante de la liste, plus bas. */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
        <CarteIcone titre="Déjà envoyées" valeur={data.envoyees} Icon={Send} teinte="sarcelle"
          testid="attest-envoyees" onClick={() => ouvrirVue("envoyees")}
          detail={`sur ${data.total} élève${data.total > 1 ? "s" : ""} concerné${data.total > 1 ? "s" : ""}`}
          enfants={(
            <div className="mt-2 flex items-center gap-2" data-testid="attest-total">
              <div className="h-1.5 rounded-full flex-1 min-w-[50px]" style={{ background: "#CCFBF1" }}>
                <div className="h-1.5 rounded-full" style={{ width: `${pourcentEnvoyees}%`, background: TEINTE.accent }} />
              </div>
              <span className="text-xs tabular-nums">{pourcentEnvoyees} %</span>
            </div>
          )} />
        <CarteIcone titre="Prêtes à envoyer" valeur={data.pretes} Icon={FileCheck2} teinte={data.pretes ? "bleu" : "neutre"}
          detail={data.pretes && auto.ok ? `facture payée, n° AVS connu · partent toutes seules ${auto.quand}`
            : "facture payée, n° AVS connu"} testid="attest-pretes" onClick={() => ouvrirVue("pretes")} />
        <CarteIcone titre="Attendent le paiement" valeur={data.attend_paiement} Icon={Wallet} teinte="neutre"
          detail="elle se prépare dès que la facture est réglée" testid="attest-attend-paiement" onClick={() => ouvrirVue("attend_paiement")} />
        {/* La vignette comptait TOUT LE MONDE (134) alors que l'onglet ne montre que les
            enfants (88) : la relance ne concerne que le dossier Jeunesse+Sport. On annonce
            donc les deux chiffres, sinon l'école croit à une erreur (école, 05.10.2026). */}
        <CarteIcone titre="Sans n° AVS" valeur={data.bloquees} Icon={AlertTriangle} teinte={data.bloquees ? "ambre" : "neutre"}
          testid="attest-bloquees" onClick={() => ouvrirVue("bloquees")}
          detail={`${sansAvsEnfants} enfant(s) à relancer · ${Math.max(data.bloquees - sansAvsEnfants, 0)} adulte(s), hors Jeunesse+Sport`} />
        <CarteIcone titre="À zéro" valeur={data.a_zero} Icon={CircleSlash} teinte="neutre"
          detail="rien à attester — non envoyées" testid="attest-a-zero" onClick={() => ouvrirVue("a_zero")} />
        {/* La facture est la source de vérité : une facture corrigée rend caduque
            l'attestation déjà partie, et c'est elle que la famille joint à ses impôts. */}
        {/* Rouge seulement s'il y a un geste à faire : une nouvelle attestation qui attend le
            paiement de la facture corrigée, ou qui part toute seule à 6h15, n'en demande aucun. */}
        <CarteIcone titre="Nouvelles à envoyer" valeur={data.a_renvoyer} Icon={RefreshCw}
          teinte={!renvoisPrets ? "neutre" : auto.ok ? "bleu" : "rouge"}
          detail={!data.a_renvoyer ? "aucune facture corrigée depuis l'envoi" : [
            renvoisPrets ? `${renvoisPrets} prête(s)${auto.ok ? ` : partent toutes seules ${auto.quand}` : " : à envoyer"}` : "",
            renvoisEnAttente ? `${renvoisEnAttente} attend(ent) le paiement de la facture corrigée` : "",
          ].filter(Boolean).join(" · ")}
          testid="attest-a-renvoyer" onClick={() => ouvrirVue("a_renvoyer")} />
      </div>

      <Encart titre="Où en est chaque élève" testid="attest-repartition"
        aide={`Chaque élève dans une seule case · ${data.enfants} enfant${data.enfants > 1 ? "s" : ""}, ${data.adultes} adulte${data.adultes > 1 ? "s" : ""}.`}>
        <Repartition testid="attest-parts" colonnes vide="Aucune attestation ce semestre pour l'instant." parts={[
          { cle: "envoyee", label: "Envoyée, à jour", couleur: "#0D9488", valeur: cases.envoyee || 0 },
          { cle: "a_refaire", label: "Envoyée, à refaire", detail: "la facture a été corrigée et renvoyée depuis", couleur: "#DC2626", valeur: cases.a_refaire || 0 },
          { cle: "prete", label: "Prête à envoyer", couleur: "#2563EB", valeur: cases.prete || 0 },
          { cle: "attend_paiement", label: "Attend le paiement de la facture", couleur: "#94A3B8", valeur: cases.attend_paiement || 0 },
          { cle: "sans_avs", label: "Bloquée : pas de n° AVS", couleur: "#F59E0B", valeur: cases.sans_avs || 0 },
          { cle: "facture_en_correction", label: "Facture en cours de correction", couleur: "#A78BFA", valeur: cases.facture_en_correction || 0 },
          { cle: "a_zero", label: "À 0 CHF : rien à attester", couleur: "#CBD5E1", valeur: cases.a_zero || 0 },
        ]} />
      </Encart>

      <div id="attest-liste" className="scroll-mt-24" />
      <Section
        titre="Élèves"
        aide="Sélectionne pour relancer les numéros AVS manquants ou envoyer les attestations."
        testid="attest-section"
        action={
          // Neuf onglets : sur téléphone ils passent à la ligne au lieu de sortir de l'écran.
          <div className="flex flex-wrap gap-1 p-1 rounded-2xl max-w-full" style={{ background: "#EEF2F6" }}>
            {FILTRES.map((f) => (
              <button key={f.id} data-testid={`attest-filtre-${f.id}`}
                onClick={() => { setFiltre(f.id); setPublic(PUBLIC_PAR_DEFAUT[f.id] || "toutes"); setChoisis([]); }}
                className="px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: filtre === f.id ? "#fff" : "transparent", color: filtre === f.id ? "var(--primary)" : "var(--muted)" }}>
                {f.label} ({compteur(f.id)})
              </button>
            ))}
          </div>
        }
      >
        {/* Deuxième niveau : l'âge affine la vue choisie ci-dessus — c'est lui qui sert
            à ne relancer que les enfants (dossier Jeunesse+Sport). */}
        <div className="flex flex-wrap items-center gap-3 px-1" data-testid="attest-publics">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              data-testid="attest-recherche"
              value={recherche}
              onChange={(e) => { setRecherche(e.target.value); setChoisis([]); }}
              placeholder="Chercher un élève, un parent, un e-mail, un cours…"
              className="pl-9 pr-3 py-2 rounded-full text-sm w-[340px] border outline-none focus:border-[var(--primary)]"
              style={{ borderColor: "var(--border)" }}
            />
          </div>
          {q && (
            <span className="text-xs text-[var(--muted)]" data-testid="attest-recherche-info">
              {items.length} résultat(s) dans toute l'école — les onglets et l'âge sont ignorés pendant la recherche
            </span>
          )}
          {!q && (
            <>
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">
                Dans cette vue
              </span>
              <div className="flex gap-1 p-1 rounded-full" style={{ background: "#EEF2F6" }}>
                {PUBLICS.map((p) => (
                  <button key={p.id} data-testid={`attest-public-${p.id}`} onClick={() => { setPublic(p.id); setChoisis([]); }}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ background: public_ === p.id ? "#fff" : "transparent", color: public_ === p.id ? "var(--primary)" : "var(--muted)" }}>
                    {p.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-[var(--muted)]">
                {items.length} élève(s) · {public_ === "enfants" ? "2 à 20 ans, dossier Jeunesse+Sport"
                  : public_ === "adultes" ? "adultes, attestation d'assurance seule" : "tous les âges"}
              </span>
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-1">
            <Case cochee={tous} onChange={(v) => setChoisis(v ? items.map((l) => l.eleve_id) : [])}
              testid="attest-tout-selectionner" titre="Tout sélectionner" />
            <span className="text-xs text-[var(--muted)]">Tout sélectionner ({items.length})</span>
            {relancables.length > 0 && (
              <button className="btn-ghost" data-testid="attest-selection-relancables"
                onClick={() => setChoisis(relancables.map((l) => l.eleve_id))}>
                Sélectionner les {relancables.length} relançable(s) — aucune demande depuis {DELAI_RELANCE} j
              </button>
            )}
          </div>
        )}

        {choisis.length > 0 && (
          <BarreSelection nb={choisis.length} testid="attest-barre">
            {dejaAvs.length > 0 ? (
              <span className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }} data-testid="attest-avs-impossible">
                {dejaAvs.length} élève(s) de la sélection ont déjà donné leur n° AVS : aucune demande
                ne peut partir. Retire-les pour relancer les autres.
              </span>
            ) : (
              <BoutonSombre onClick={() => agir("avs")} disabled={occupe} testid="attest-demander-avs">
                <Send size={14} /> {occupe ? "Envoi en cours…" : "Demander le n° AVS à toute la sélection"}
              </BoutonSombre>
            )}
            <BoutonSombre onClick={() => agir("envoyer")} disabled={occupe} testid="attest-envoyer">
              <Send size={14} /> Envoyer l'attestation
            </BoutonSombre>
          </BarreSelection>
        )}

        <Tableau
          testid="tableau-attestations"
          fixe
          entetes={[{ label: "", largeur: "4%" }, { label: "Élève", largeur: "15%" },
            { label: "Responsable", largeur: "20%" }, { label: "Cours", largeur: "17%" },
            { label: "Montant", droite: true, largeur: "10%" },
            { label: "N° AVS", largeur: "18%" },
            { label: "Attestation", largeur: "16%" }]}
          lignes={items}
          vide="Personne dans cette vue."
          surligne={(l) => !!vise?.eleve && l.eleve_id === vise.eleve}
          rendu={(l) => (
            <>
              <Cellule>
                <Case cochee={choisis.includes(l.eleve_id)} testid="attest-case"
                  onChange={(v) => setChoisis(v ? [...choisis, l.eleve_id] : choisis.filter((x) => x !== l.eleve_id))} />
              </Cellule>
              <Cellule fort>{l.eleve}
                <div className="text-xs font-normal text-[var(--muted)]">
                  {l.age !== null && l.age !== undefined ? `${l.age} ans · ` : ""}{l.enfant ? "enfant" : "adulte"}
                </div>
              </Cellule>
              <Cellule>{l.responsable}<div className="text-xs text-[var(--muted)]">{l.email}</div></Cellule>
              <Cellule muet>
                <span title={l.cours.join(", ")}>
                  {l.cours.slice(0, 2).join(", ")}{l.cours.length > 2 ? ` +${l.cours.length - 2}` : ""}
                </span>
              </Cellule>
              <Cellule droite>
                <span className="whitespace-nowrap">{argent(l.montant)}</span>
                {Math.abs(l.reduction) > 0.005 && (
                  <div className="text-[11px] text-[var(--muted)] whitespace-nowrap" title="Réductions et taxe administrative de la famille, ramenées à cet élève">
                    {l.montant_plein.toFixed(0)} {l.reduction > 0 ? "−" : "+"} {Math.abs(l.reduction).toFixed(0)}
                  </div>
                )}
                {/* Ce que la famille a déjà reçu, quand la facture a changé depuis. */}
                {l.a_renvoyer && (
                  <div className="text-[11px] whitespace-nowrap" style={{ color: "#B91C1C" }}
                    data-testid="attest-ecart-envoye">
                    déjà envoyée : {argent(l.montant_envoye)}
                  </div>
                )}
              </Cellule>
              {/* Numéro AVS et dernière demande dans une seule colonne : à huit colonnes,
                  le tableau dépassait l'écran et le bouton « Aperçu » se retrouvait coupé. */}
              <Cellule>
                {l.avs ? <Badge ton="vert"><ShieldCheck size={12} /> {l.avs}</Badge>
                  : <Badge ton="ambre">manquant</Badge>}
                <div className="text-[11px] text-[var(--muted)] mt-1" data-testid="attest-derniere-demande"
                  title={`${l.avs_demande_source || "demandé"} · aucune nouvelle demande avant ${DELAI_RELANCE} jours`}>
                  {l.avs_demande_le
                    ? `demandé le ${l.avs_demande_le.split("-").reverse().join(".")}`
                      + (joursDepuis(l.avs_demande_le) < DELAI_RELANCE
                        ? ` · il y a ${joursDepuis(l.avs_demande_le)} j` : " · relançable")
                    : "jamais demandé"}
                </div>
              </Cellule>
              {/* Statut et aperçu dans une seule colonne : le tableau tenait sinon plus
                  large que l'écran et le bouton « Aperçu » se retrouvait coupé. */}
              <Cellule>
                <StatutAttestation ligne={l}
                  onApercu={() => ouvrirPdf(`/attestations/${encodeURIComponent(l.eleve_id)}/pdf`
                    + `?semestre=${semestre}${saison ? `&saison=${saison}` : ""}`,
                  `Attestation — ${l.eleve}`)} />
              </Cellule>
            </>
          )}
        />
      </Section>
    </div>
  );
}
